import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cmsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, MailCheck } from 'lucide-react';
import { scheduleReleaseUiScrollLock } from '@/utils/releaseUiScrollLock';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  /** Efter lyckad registrering: fast text i stället för försvinnande toast */
  const [registerSuccessMessage, setRegisterSuccessMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    cmsApi.getMaintenanceStatus().then((res) => {
      if (!cancelled) setMaintenanceMode(Boolean(res.data?.maintenance_mode));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (maintenanceMode && activeTab === 'register') setActiveTab('login');
  }, [maintenanceMode, activeTab]);

  useEffect(() => () => scheduleReleaseUiScrollLock(), []);

  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success('Välkommen tillbaka!');
      scheduleReleaseUiScrollLock();
      if (maintenanceMode && userData?.is_admin) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Felaktig e-post eller lösenord');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Lösenorden matchar inte');
      return;
    }
    if (!registerData.acceptTerms) {
      toast.error('Du måste godkänna användarvillkoren');
      return;
    }

    setLoading(true);
    try {
      const { message } = await register({
        name: registerData.name,
        email: registerData.email,
        password: registerData.password,
        account_type: 'private',
        accept_terms: true,
      });
      const verificationText =
        message ||
        'Konto skapat. Öppna länken i mailet vi skickat till dig och verifiera e-posten innan du loggar in.';
      setRegisterSuccessMessage(verificationText);
      setLoginData((prev) => ({ ...prev, email: registerData.email }));
      setRegisterData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false,
      });
      setActiveTab('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte skapa konto');
    } finally {
      setLoading(false);
    }
  };

  const setReg = (field, value) => setRegisterData({ ...registerData, [field]: value });

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 mb-6">
            <span className="text-3xl font-black text-[#003B73]">Bygg</span>
            <span className="text-3xl font-black text-[#FF8A00]">Fynd</span>
          </Link>
          <h1 className="text-2xl font-black text-[#0F172A]">
            {activeTab === 'login' ? 'Logga in' : 'Skapa konto'}
          </h1>
          <p className="text-gray-500 mt-2">
            {activeTab === 'login' 
              ? 'Välkommen tillbaka! Logga in på ditt konto.' 
              : 'Registrera dig för att börja handla på ByggFynd.'}
          </p>
        </div>

        <div className="bg-white rounded-md shadow-sm p-8">
          {maintenanceMode ? (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              <strong>Underhållsläge.</strong> Endast administratörer kan logga in. Nyregistrering är avstängd tills vidare.
            </div>
          ) : null}
          {registerSuccessMessage ? (
            <div
              className="mb-6 rounded-xl border-2 border-[#0f766e] bg-[#ecfdf5] px-4 py-4 text-[#064e3b] shadow-sm"
              role="status"
              aria-live="polite"
              data-testid="register-success-banner"
            >
              <div className="flex gap-3">
                <MailCheck className="h-6 w-6 flex-shrink-0 text-[#0d9488] mt-0.5" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-base text-[#042f2e] mb-2">Kolla din e-post</p>
                  <p className="text-sm leading-relaxed">{registerSuccessMessage}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-4 border-[#0d9488] text-[#0f766e] hover:bg-[#ccfbf1]"
                    onClick={() => setRegisterSuccessMessage(null)}
                  >
                    Stäng meddelandet
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2 px-4 rounded-sm font-semibold transition-colors ${
                activeTab === 'login' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="login-tab"
            >
              Logga in
            </button>
            {!maintenanceMode ? (
              <button
                type="button"
                onClick={() => setActiveTab('register')}
                className={`flex-1 py-2 px-4 rounded-sm font-semibold transition-colors ${
                  activeTab === 'register' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="register-tab"
              >
                Skapa konto
              </button>
            ) : null}
          </div>

          {activeTab === 'login' ? (
            /* LOGIN */
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="login-email">E-postadress</Label>
                <Input id="login-email" type="email" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required className="mt-1" placeholder="din@email.se" data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="login-password">Lösenord</Label>
                <div className="relative mt-1">
                  <Input id="login-password" type={showPassword ? 'text' : 'password'} value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required placeholder="********" data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-[#003B73] hover:bg-[#002a52] font-bold" data-testid="login-submit">
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loggar in...</> : 'Logga in'}
              </Button>
              <div className="text-center">
                <Link to="/glomt-losenord" className="text-sm text-[#003B73] hover:underline font-medium" data-testid="forgot-password-link">
                  Glömt lösenord?
                </Link>
              </div>
            </form>
          ) : (
            /* REGISTER */
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="register-name">Fullständigt namn *</Label>
                <Input id="register-name" type="text" value={registerData.name} onChange={(e) => setReg('name', e.target.value)} required className="mt-1" data-testid="register-name" />
              </div>

              <div>
                <Label htmlFor="register-email">E-postadress *</Label>
                <Input id="register-email" type="email" value={registerData.email} onChange={(e) => setReg('email', e.target.value)} required className="mt-1" data-testid="register-email" />
              </div>

              <div>
                <Label htmlFor="register-password">Lösenord *</Label>
                <div className="relative mt-1">
                  <Input id="register-password" type={showPassword ? 'text' : 'password'} value={registerData.password} onChange={(e) => setReg('password', e.target.value)} required minLength={6} data-testid="register-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="register-confirm">Bekräfta lösenord *</Label>
                <Input id="register-confirm" type="password" value={registerData.confirmPassword} onChange={(e) => setReg('confirmPassword', e.target.value)} required className="mt-1" data-testid="register-confirm-password" />
              </div>

              <div className="flex items-start gap-2">
                <Checkbox id="register-terms" checked={registerData.acceptTerms} onCheckedChange={(checked) => setReg('acceptTerms', Boolean(checked))} data-testid="accept-terms" />
                <label htmlFor="register-terms" className="text-sm text-gray-600 leading-tight">
                  Jag har tagit del av och godkänner{' '}
                  <Link to="/anvandarvillkor-kund" target="_blank" rel="noopener noreferrer" className="text-[#003B73] hover:underline font-medium">
                    användarvillkoren för kunder
                  </Link>
                  {' '}samt{' '}
                  <Link to="/integritetspolicy" target="_blank" rel="noopener noreferrer" className="text-[#003B73] hover:underline font-medium">
                    integritetspolicyn
                  </Link>
                  .
                </label>
              </div>

              <Button type="submit" disabled={loading} className="w-full h-12 bg-[#FF8A00] hover:bg-[#e67c00] font-bold" data-testid="register-submit">
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Skapar konto...</> : 'Skapa konto'}
              </Button>
            </form>
          )}

          {activeTab === 'login' && !maintenanceMode && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-500 text-center mb-2">Demo-inloggning (admin)</p>
              <p className="text-xs text-gray-600 text-center">admin@byggfynd.se / admin123</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
